import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Avatar,
  Tooltip,
  Drawer,
  Typography,
  Card,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import * as MuiIcons from "@mui/icons-material";
import StarIcon from "@mui/icons-material/Star";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PersonIcon from "@mui/icons-material/Person";
import { groupNameFor } from "./Utils/neighborhood.utils";

/* ------------------------------ Shared Style ----------------------------- */

const pageCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  backgroundColor: "rgba(255,255,255,0.96)",
  overflow: "hidden",
};

const secondaryButtonSx = {
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  px: 1.5,
  borderColor: "rgba(47,99,173,0.18)",
  color: "#223142",
  whiteSpace: "nowrap",
  "&:hover": {
    borderColor: "#2f63ad",
    color: "#2f63ad",
    backgroundColor: "rgba(47,99,173,0.04)",
  },
};

const chipSx = {
  borderRadius: "999px",
};

const toolbarButtonSx = {
  textTransform: "none",
  borderRadius: "10px",
  color: "#223142",
};

/* ------------------------------- Helpers -------------------------------- */

const buildAssignees = (order, getAssignedUserName) => {
  if (Array.isArray(order?.assignees_details) && order.assignees_details.length) {
    return order.assignees_details.map((a) => ({
      id: a.user_id,
      name: `${a.name || ""} ${a.lastname || ""}`.trim() || `#${a.user_id}`,
      isPrimary: !!a.is_primary,
    }));
  }

  if (Array.isArray(order?.assignees) && order.assignees.length) {
    return order.assignees.map((id, idx) => ({
      id,
      name: getAssignedUserName ? getAssignedUserName(id) || `#${id}` : `#${id}`,
      isPrimary: idx === 0,
    }));
  }

  if (order?.assign_to != null) {
    const id = order.assign_to;
    return [
      {
        id,
        name: getAssignedUserName ? getAssignedUserName(id) || `#${id}` : `#${id}`,
        isPrimary: true,
      },
    ];
  }

  return [];
};

const initialsOf = (full) =>
  (full || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("") || "?";

/* ------------------------------- Toolbar -------------------------------- */

const TableToolbar = () => (
  <GridToolbarContainer
    sx={{
      padding: "12px 12px 8px 12px",
      borderBottom: "1px solid rgba(47,99,173,0.08)",
      backgroundColor: "rgba(255,255,255,0.9)",
      gap: 1,
      flexWrap: "wrap",
    }}
  >
    <GridToolbarColumnsButton sx={toolbarButtonSx} />
    <GridToolbarFilterButton sx={toolbarButtonSx} />
    <GridToolbarDensitySelector sx={toolbarButtonSx} />
    <GridToolbarExport sx={toolbarButtonSx} />
  </GridToolbarContainer>
);

/* ------------------------------ Main Table ------------------------------ */

const WorkOrderTable = ({
  rows = [],
  getAssignedUserName,
  getTypeName,
  getStatusInfo,
}) => {
  const [selectedRow, setSelectedRow] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);

  useEffect(() => {
    const savedVisibility = localStorage.getItem("workOrderTable_columnVisibility");
    const savedOrder = localStorage.getItem("workOrderTable_columnOrder");

    if (savedVisibility) setColumnVisibilityModel(JSON.parse(savedVisibility));
    if (savedOrder) setColumnOrder(JSON.parse(savedOrder));
  }, []);

  const normalizedRows = useMemo(() => {
    return rows.map((o) => {
      const openedByLabel =
        o.opened_by_name ||
        (o.opened_by_user_id != null && getAssignedUserName
          ? getAssignedUserName(o.opened_by_user_id)
          : null) ||
        (o.opened_by_user_id != null ? `#${o.opened_by_user_id}` : null) ||
        "Unknown";

      return {
        ...o,
        full_address: o.full_address || o.address || "N/A",
        ClubMembershipTypeCode:
          o.ClubMembershipTypeCode || o.club_membership_type_code || "",
        fullname:
          (o.fullname || `${o.FirstName || ""} ${o.LastName || ""}`)
            .replace(/\s+/g, " ")
            .trim() || "N/A",
        description: o.description || "N/A",
        details: o.details || "",
        opened_by_display: openedByLabel,
        created_at: o.created_at
          ? new Date(o.created_at).toISOString().slice(0, 10)
          : "",
        resolved_at: o.resolved_at
          ? new Date(o.resolved_at).toISOString().slice(0, 10)
          : "",
      };
    });
  }, [rows, getAssignedUserName]);

  const columns = [
    {
      field: "order_id",
      headerName: "Work Order ID",
      width: 150,
      renderCell: (params) =>
        params.value ? (
          <Button
            component={Link}
            to={`${params.value}`}
            state={{ from: "/home/workorders" }}
            variant="text"
            size="small"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: "#2f63ad",
              px: 0.5,
              minWidth: "auto",
              "&:hover": {
                backgroundColor: "rgba(47,99,173,0.04)",
              },
            }}
          >
            WO-{params.value}
          </Button>
        ) : (
          "N/A"
        ),
    },
    {
      field: "property_id",
      headerName: "Property ID",
      width: 140,
    },
    {
      field: "fullname",
      headerName: "Member Name",
      width: 200,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "full_address",
      headerName: "Address",
      width: 240,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "ClubMembershipTypeCode",
      headerName: "Membership Type",
      width: 180,
      renderCell: (params) => (params.value ? groupNameFor(params.value) : "N/A"),
    },
    {
      field: "priority",
      headerName: "Priority",
      width: 130,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "opened_by_display",
      headerName: "Opened By",
      width: 220,
      renderCell: (params) => {
        const label = params.value || "Unknown";
        return (
          <Tooltip title={`Opened by ${label}`}>
            <Chip
              size="small"
              variant="outlined"
              label={label}
              avatar={
                <Avatar
                  sx={{
                    width: 22,
                    height: 22,
                    fontSize: 11,
                    bgcolor: "grey.300",
                  }}
                >
                  {initialsOf(label)}
                </Avatar>
              }
              sx={chipSx}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "assign_to",
      headerName: "Assigned To",
      width: 280,
      renderCell: (params) => {
        const assignees = buildAssignees(params.row, getAssignedUserName);

        if (!assignees.length) {
          return (
            <Chip
              label="Not Assigned"
              size="small"
              variant="outlined"
              sx={chipSx}
            />
          );
        }

        return (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", py: 0.5 }}>
            {assignees.map((a) => {
              const label = a.name || `#${a.id}`;
              return (
                <Tooltip
                  key={a.id}
                  title={a.isPrimary ? `${label} (Primary)` : label}
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    label={label}
                    avatar={
                      <Avatar
                        sx={{
                          width: 22,
                          height: 22,
                          fontSize: 11,
                          bgcolor: a.isPrimary ? "primary.main" : "grey.300",
                          color: a.isPrimary
                            ? "primary.contrastText"
                            : "text.primary",
                        }}
                      >
                        {initialsOf(label)}
                      </Avatar>
                    }
                    icon={a.isPrimary ? <StarIcon fontSize="small" /> : undefined}
                    sx={{
                      ...chipSx,
                      "& .MuiChip-icon": { ml: 0.25 },
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        );
      },
    },
    {
      field: "workorder_type_id",
      headerName: "Type",
      width: 160,
      renderCell: (params) => getTypeName?.(params.value) || "N/A",
    },
    {
      field: "subtype_name",
      headerName: "Subtype",
      width: 160,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "location_name",
      headerName: "Location",
      width: 160,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "created_at",
      headerName: "Created Date",
      width: 140,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "resolved_at",
      headerName: "Resolved Date",
      width: 140,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "details",
      headerName: "Details",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box
          sx={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.4,
            py: 1,
          }}
        >
          {params?.value || "N/A"}
        </Box>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 250,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "status",
      headerName: "Status",
      width: 210,
      renderCell: (params) => {
        const statusInfo = getStatusInfo?.(params.row?.status) || {};
        const IconComponent = statusInfo.icon && MuiIcons[statusInfo.icon];

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {IconComponent && (
              <IconComponent
                fontSize="small"
                sx={{ color: statusInfo?.color || "#5f6b7a" }}
              />
            )}
            <Chip
              label={statusInfo.statusname || "Unknown"}
              size="small"
              variant="outlined"
              sx={{
                borderRadius: "999px",
                fontWeight: 600,
                color: "#223142",
                backgroundColor: "rgba(255,255,255,0.96)",
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 160,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => setSelectedRow(params.row)}
          sx={secondaryButtonSx}
        >
          View Details
        </Button>
      ),
    },
  ];

  const orderedColumns =
    columnOrder.length > 0
      ? [
          ...columnOrder
            .map((field) => columns.find((col) => col.field === field))
            .filter(Boolean),
          ...columns.filter((col) => !columnOrder.includes(col.field)),
        ]
      : columns;

  const handleVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem("workOrderTable_columnVisibility", JSON.stringify(newModel));
  };

  const handleColumnOrderChange = (params) => {
    const newOrder = params.api
      .getAllColumns()
      .map((col) => col.field)
      .filter((field) => field !== "__check__");

    setColumnOrder(newOrder);
    localStorage.setItem("workOrderTable_columnOrder", JSON.stringify(newOrder));
  };

  return (
    <>
      <Card sx={pageCardSx}>
        <Box
          sx={{
            height: 5,
            backgroundColor: "#2f63ad",
          }}
        />

        <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <AssignmentIcon sx={{ color: "#2f63ad", fontSize: 20 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#223142",
                  lineHeight: 1.2,
                }}
              >
                Work Orders Table
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              Review, filter, export, and inspect work order records
            </Typography>
          </Box>

          <Box sx={{ width: "100%" }}>
            <DataGrid
              rows={[...normalizedRows].sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at)
              )}
              columns={orderedColumns}
              getRowId={(row) => row.order_id}
              pageSize={10}
              rowsPerPageOptions={[10, 20, 50]}
              disableSelectionOnClick
              autoHeight
              disableColumnReorder={false}
              disableColumnResize={false}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={handleVisibilityChange}
              onColumnOrderChange={handleColumnOrderChange}
              slots={{ toolbar: TableToolbar }}
              sx={{
                border: "1px solid rgba(47,99,173,0.08)",
                borderRadius: "14px",
                backgroundColor: "#fff",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "rgba(47,99,173,0.04)",
                  color: "#223142",
                  fontWeight: 700,
                  borderBottom: "1px solid rgba(47,99,173,0.08)",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                },
                "& .MuiDataGrid-row": {
                  borderBottom: "1px solid rgba(47,99,173,0.06)",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "rgba(47,99,173,0.03)",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "none",
                  alignItems: "center",
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: "1px solid rgba(47,99,173,0.08)",
                  backgroundColor: "rgba(255,255,255,0.92)",
                },
                "& .MuiButton-root": {
                  textTransform: "none",
                },
              }}
            />
          </Box>
        </Box>
      </Card>

      <Drawer
        anchor="right"
        open={!!selectedRow}
        onClose={() => setSelectedRow(null)}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: 420 },
            p: 0,
            backgroundColor: "#f8fafc",
          },
        }}
      >
        {selectedRow ? (
          <>
            <Box
              sx={{
                height: 5,
                backgroundColor: "#2f63ad",
              }}
            />
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <AssignmentIcon sx={{ color: "#2f63ad", fontSize: 20 }} />
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#223142" }}
                >
                  Work Order Details
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ color: "#6b778c", mb: 3 }}>
                Detailed view for selected work order
              </Typography>

              <Box sx={{ display: "grid", gap: 1.5 }}>
                <Typography><strong>ID:</strong> {selectedRow.order_id || "N/A"}</Typography>
                <Typography><strong>Property:</strong> {selectedRow.property_id || "N/A"}</Typography>
                <Typography><strong>Member:</strong> {selectedRow.fullname || "N/A"}</Typography>
                <Typography><strong>Address:</strong> {selectedRow.full_address || "N/A"}</Typography>
                <Typography>
                  <strong>Membership Type:</strong>{" "}
                  {selectedRow.ClubMembershipTypeCode
                    ? groupNameFor(selectedRow.ClubMembershipTypeCode)
                    : "N/A"}
                </Typography>
                <Typography>
                  <strong>Opened By:</strong> {selectedRow.opened_by_display || "Unknown"}
                </Typography>
                <Typography>
                  <strong>Type:</strong> {getTypeName?.(selectedRow.workorder_type_id) || "N/A"}
                </Typography>
                <Typography><strong>Subtype:</strong> {selectedRow.subtype_name || "N/A"}</Typography>
                <Typography><strong>Location:</strong> {selectedRow.location_name || "N/A"}</Typography>
                <Typography><strong>Status:</strong> {selectedRow.status || "N/A"}</Typography>
                <Typography><strong>Created:</strong> {selectedRow.created_at || "N/A"}</Typography>
                <Typography><strong>Resolved:</strong> {selectedRow.resolved_at || "N/A"}</Typography>

                <Box sx={{ mt: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
                    Assigned To
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                    {buildAssignees(selectedRow, getAssignedUserName).length ? (
                      buildAssignees(selectedRow, getAssignedUserName).map((a) => {
                        const label = a.name || `#${a.id}`;
                        return (
                          <Chip
                            key={a.id}
                            size="small"
                            variant="outlined"
                            label={label}
                            avatar={
                              <Avatar
                                sx={{
                                  width: 22,
                                  height: 22,
                                  fontSize: 11,
                                  bgcolor: a.isPrimary ? "primary.main" : "grey.300",
                                  color: a.isPrimary
                                    ? "primary.contrastText"
                                    : "text.primary",
                                }}
                              >
                                {initialsOf(label)}
                              </Avatar>
                            }
                            icon={a.isPrimary ? <StarIcon fontSize="small" /> : undefined}
                            sx={{
                              ...chipSx,
                              "& .MuiChip-icon": { ml: 0.25 },
                            }}
                          />
                        );
                      })
                    ) : (
                      <Chip
                        label="Not Assigned"
                        size="small"
                        variant="outlined"
                        sx={chipSx}
                      />
                    )}
                  </Box>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
                    Details
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", color: "#223142", lineHeight: 1.6 }}
                  >
                    {selectedRow.details || "N/A"}
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
                    Description
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", color: "#223142", lineHeight: 1.6 }}
                  >
                    {selectedRow.description || "N/A"}
                  </Typography>
                </Box>

                <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                  <Button
                    component={Link}
                    to={`${selectedRow.order_id}`}
                    state={{ from: "/home/workorders" }}
                    variant="outlined"
                    size="small"
                    sx={secondaryButtonSx}
                  >
                    Open Full Page
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setSelectedRow(null)}
                    sx={{ textTransform: "none", color: "#5f6b7a" }}
                  >
                    Close
                  </Button>
                </Box>
              </Box>
            </Box>
          </>
        ) : null}
      </Drawer>
    </>
  );
};

export default WorkOrderTable;